import dayjs from "dayjs";
import { PriceBookWithCounts } from "../../../../types/price-book.type";
import { toEffectiveMode } from "../validityModel";
import { PriceBookDrawerMode, PriceBookFormState } from "../types";

/** Valid every day from the save on: a new book's validity, and a copy's (it never inherits one). */
export const INITIAL_VALIDITY: Pick<
  PriceBookFormState,
  "effectiveMode" | "effectiveFrom" | "effectiveTo"
> = {
  effectiveMode: "now",
  effectiveFrom: null,
  effectiveTo: null,
};

export const INITIAL_FORM: PriceBookFormState = {
  code: "",
  name: "",
  type: "sell",
  status: "enable",
  discountPercent: "0",
  ...INITIAL_VALIDITY,
};

export const TITLE_BY_MODE: Record<PriceBookDrawerMode, string> = {
  create: "เพิ่มสมุดราคา",
  edit: "แก้ไขสมุดราคา",
  duplicate: "ทำสำเนาเป็นเล่มใหม่",
};

// Announced once, after the save has landed.
export const PRICE_BOOK_SAVED_NOTICE = "บันทึกสมุดราคาแล้ว";

export const SAVE_FAILED =
  "บันทึกสมุดราคาไม่สำเร็จ ยังไม่มีการเปลี่ยนแปลงใดถูกบันทึก";
export const DUPLICATE_FAILED =
  "ทำสำเนาสมุดราคาไม่สำเร็จ ยังไม่มีการสร้างสำเนาใด";

// The prefilled code is only a suggestion: two people creating at once are handed the
// same one and the API refuses the second POST, so the drawer asks for another code.
export const CODE_TAKEN_RETRY =
  "รหัสสมุดราคานี้ถูกใช้งานไปแล้ว ระบบได้ใส่รหัสใหม่ให้แล้ว กรุณากดบันทึกอีกครั้ง";

// An edit sends the header back as loaded, so it must not be sent before it was read.
export const LOAD_DETAILS_FAILED =
  "โหลดข้อมูลสมุดราคาไม่สำเร็จ จึงยังบันทึกไม่ได้ กรุณาปิดหน้าต่างนี้แล้วเปิดใหม่อีกครั้ง";

// A create has no typed code to fall back on, so failing to read one is the same dead
// end as a failed detail load.
export const NEXT_CODE_FAILED =
  "สร้างรหัสสมุดราคาอัตโนมัติไม่สำเร็จ จึงยังบันทึกไม่ได้ กรุณาปิดหน้าต่างนี้แล้วเปิดใหม่อีกครั้ง";

// A new book, and a copy, can only be tried in 00-HO before any branch's shop is put on it.
export const HEAD_OFFICE_START_NOTICE = "สมุดราคาใหม่เริ่มใช้ได้เฉพาะ 00-HO";
export const DUPLICATE_START_NOTICE =
  "สำเนาเริ่มใช้ได้เฉพาะ 00-HO และไม่มีรายชื่อลูกค้า เนื่องจากลูกค้าหนึ่งรายใช้ได้เพียงสมุดราคาเดียว";

/** The header form of a stored pricebook; a copy starts from the source's name, type and %. */
export const toPriceBookForm = (
  priceBook: PriceBookWithCounts,
): PriceBookFormState => ({
  code: priceBook.code ?? "",
  name: priceBook.name ?? "",
  type: priceBook.type ?? null,
  // Rows the vansale sync created carry no status, and the API reads that as enabled.
  status: priceBook.status ?? "enable",
  discountPercent: String(priceBook.discount_percent),
  effectiveMode: toEffectiveMode(priceBook),
  effectiveFrom: priceBook.effective_from ? dayjs(priceBook.effective_from) : null,
  effectiveTo: priceBook.effective_to ? dayjs(priceBook.effective_to) : null,
});
